import {excelFileToRows} from 'common/HelperFunctions'
import {
  dispatchApi,
  isAbortError,
  isChunkedUploadAvailable,
  withUploadedFile,
} from 'common/helpers/chunkedUpload'
import {
  DATA_UPLOAD_ERROR_KEYS,
  type DataUploadCounts,
  type DataUploadSkippedRow,
  dataUploadCountsSummary,
  dataUploadErrorCode,
  dataUploadErrorMessage,
  dataUploadSkippedRowKey,
} from 'common/helpers/dataUpload'
import {
  isDataUploadConversionAvailable,
  useDataUploadConfig,
} from 'common/hooks/useDataUploadConfig'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  CloudUpload,
  Download,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Info,
  Loader2,
  X,
} from 'lucide-react'
import React, {FC, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {toast} from 'react-toastify'
import {extractOnboardingTeachers} from 'setup/redux/Actions/Onboarding/extractOnboardingTeachers'
import {prefillOnboardingFromFile} from 'setup/redux/Actions/Onboarding/prefillOnboardingFromFile'

import {prefillJobRowsFound, waitForPrefillJob} from '../hooks/useOnboardingPrefillDraft'
import {
  TEACHER_UPLOAD_EXTRACTION_ENABLED,
  TEACHER_UPLOAD_LIMITS,
  TeacherEntryRow,
  TeacherExtraction,
  TeacherFileKind,
  TeacherFileRejection,
  classifyTeacherFile,
  fileExtension,
  formatBytes,
  namesToTeacherRows,
  normalizeExtractionResponse,
  normalizePastedNames,
  parseCsvRows,
  parseTeachersRows,
  splitTeacherNames,
  teacherUploadAccept,
  validateTeacherFiles,
} from '../utils/teacherEntry'

/**
 * "Add Teachers" entry screen of the Teacher Allocation step.
 *
 * Two ways in, one way out: names typed/pasted into the numbered field and files dropped
 * on the upload card are both turned into review rows, merged, and handed to the parent
 * through `onReview` when the user presses Review Teachers. The parent (Step4Manual)
 * appends them to `manualData.teachers` and switches to its review table, where duplicate
 * names and low-confidence extractions are resolved.
 *
 * Files are processed as soon as they are added. On the AI servers every file (spreadsheets
 * too) is uploaded in 512 KB pieces and read by `onboarding/extract-teachers`: a sheet with
 * headers is read without the model in about 2 s, and every row rule stays on the server. The
 * same upload is handed to `onboarding/prefill`, which reads it again in the background for the
 * Students and Teacher Allocation steps. Elsewhere the upload stays Excel-only and the sheet is
 * parsed in the browser, with the same row rules (utils/recordRules.ts).
 */

interface Props {
  isDark: boolean
  /** Teachers already in the list (resume, or coming back from Review to add more). */
  existingCount: number
  showBackButton: boolean
  onBack: () => void
  onReview: (rows: TeacherEntryRow[]) => void
  onDownloadTemplate: () => void
}

type UploadStatus = 'parsing' | 'uploading' | 'extracting' | 'ready' | 'error'

interface UploadItem {
  id: string
  file: File
  kind: TeacherFileKind
  status: UploadStatus
  rows: TeacherEntryRow[]
  /** 0..1 while `status === 'uploading'`. */
  progress?: number
  /** i18n id of the failure message when `status === 'error'`. */
  errorKey?: string
  /** Already-translated failure copy (a backend code mapped to text); wins over `errorKey`. */
  errorText?: string
  /** What was read: "Read 52 rows. 50 teachers are ready. 1 line is a heading…". */
  summary?: string | null
  /**
   * What else the file holds, read by `onboarding/prefill` for the later steps: `checking`
   * while its drafts are being read, then how many students / allocations were found.
   */
  carried?: {checking: boolean; students: number; allocation: number}
  /** Rows left out of the file, with the reason. */
  skipped?: DataUploadSkippedRow[]
}

/** Rows listed under a file before "…and N more". */
const MAX_LISTED_SKIPPED = 50

const useMockExtraction =
  String(import.meta.env.VITE_MOCK_ONBOARDING_TEACHER_EXTRACTION ?? '').toLowerCase() === 'true'

/** One file at a time: no `multiple` on the picker, and a new drop replaces the queued file. */
const singleFile = TEACHER_UPLOAD_LIMITS.maxFiles === 1

const REJECTION_KEYS: Record<TeacherFileRejection, string> = {
  unsupported: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_UNSUPPORTED',
  'too-large': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_TOO_LARGE',
  'too-many': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_TOO_MANY',
  duplicate: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_DUPLICATE',
}

/** `counts` for a sheet the browser parsed, so it gets the same summary as a server read. */
const browserCounts = (kept: number, skipped: DataUploadSkippedRow[]): DataUploadCounts => {
  const leftOut: Record<string, number> = {}
  skipped.forEach((row) => {
    const code = row.code ?? row.reason ?? ''
    if (code) leftOut[code] = (leftOut[code] ?? 0) + 1
  })
  return {read: kept + skipped.length, kept, leftOut, needValues: {}}
}

const ERROR_KEYS = {
  read: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_READ',
  noNames: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_NO_NAMES',
  noHeader: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_NO_HEADER',
  extract: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_EXTRACT',
} as const

let uploadSeq = 0
const nextUploadId = () => `teacher-upload-${Date.now()}-${++uploadSeq}`

/** Fake extraction result for local UI work while the endpoint is being built. */
const buildMockExtraction = (fileName: string) => ({
  files: [
    {
      fileName,
      status: 'ok',
      teachers: [
        {name: 'Emma Wilson', employeeId: 'EMP-101', subject: 'Mathematics', confidence: 0.97},
        {
          name: 'Oliver Bennett',
          employeeId: '',
          subject: 'Physics',
          class: 'Grade 9',
          confidence: 0.91,
        },
        {name: 'Sophie Tumer', employeeId: 'EMP-1O3', confidence: 0.54},
      ],
    },
  ],
})

const FileKindIcon: FC<{kind: TeacherFileKind; size?: number}> = ({kind, size = 18}) => {
  if (kind === 'spreadsheet') return <FileSpreadsheet size={size} aria-hidden />
  if (kind === 'image') return <ImageIcon size={size} aria-hidden />
  return <FileText size={size} aria-hidden />
}

const Step4TeacherEntry: FC<Props> = ({
  isDark,
  existingCount,
  showBackButton,
  onBack,
  onReview,
  onDownloadTemplate,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const textareaId = useId()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)

  const [namesText, setNamesText] = useState('')
  const [files, setFiles] = useState<UploadItem[]>([])
  const [isDragging, setIsDragging] = useState(false)

  // Word / PDF / photo extraction needs the AI server's endpoint (or the local mock).
  const extractionEnabled =
    useMockExtraction || (TEACHER_UPLOAD_EXTRACTION_ENABLED && isDataUploadConversionAvailable())
  // Limits come from `data-upload/use-cases`, never hardcoded; defaults until it answers.
  const uploadConfig = useDataUploadConfig('teachers', extractionEnabled && !useMockExtraction)
  const maxFileBytes = extractionEnabled
    ? uploadConfig.maxFileBytes
    : TEACHER_UPLOAD_LIMITS.maxFileBytes

  const names = useMemo(() => splitTeacherNames(namesText), [namesText])
  const lineCount = namesText.length === 0 ? 0 : namesText.split('\n').length
  const fileRowCount = files.reduce((sum, f) => sum + (f.status === 'ready' ? f.rows.length : 0), 0)
  const isBusy = files.some(
    (f) => f.status === 'parsing' || f.status === 'uploading' || f.status === 'extracting'
  )
  const hasInput = names.length > 0 || fileRowCount > 0 || existingCount > 0
  const canReview = hasInput && !isBusy

  // --- Typed / pasted names -------------------------------------------------------

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const normalized = normalizePastedNames(e.clipboardData.getData('text'))
    if (normalized === null) return
    e.preventDefault()
    const el = e.currentTarget
    const start = el.selectionStart
    const end = el.selectionEnd
    const before = namesText.slice(0, start)
    const after = namesText.slice(end)
    // Pasted names always land on their own lines, whatever the caret was touching.
    const prefix = before && !before.endsWith('\n') ? '\n' : ''
    const suffix = after && !after.startsWith('\n') ? '\n' : ''
    const inserted = prefix + normalized + suffix
    setNamesText(before + inserted + after)
    const caret = start + inserted.length
    requestAnimationFrame(() => el.setSelectionRange(caret, caret))
  }

  const syncGutterScroll = () => {
    if (gutterRef.current && textareaRef.current) {
      gutterRef.current.scrollTop = textareaRef.current.scrollTop
    }
  }

  // --- Files ----------------------------------------------------------------------

  const patchFile = useCallback((id: string, patch: Partial<UploadItem>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? {...f, ...patch} : f)))
  }, [])

  /** One AbortController per file being uploaded: removing or replacing it stops the pieces. */
  const uploadAbortsRef = useRef(new Map<string, AbortController>())
  /** Per file: stops watching its prefill drafts when the file is removed or replaced. */
  const prefillAbortsRef = useRef(new Map<string, AbortController>())
  useEffect(() => {
    const prefills = prefillAbortsRef.current
    return () => prefills.forEach((controller) => controller.abort())
  }, [])

  /**
   * After `onboarding/prefill` answers, wait for its Students and Allocation drafts and say
   * how many rows each holds, so the user knows the file will fill those steps too. Only a
   * count: the steps themselves fetch and apply the drafts when they open.
   */
  const watchCarried = useCallback(
    (item: UploadItem, prefillResponse: any) => {
      const raw = prefillResponse?.result?.drafts ?? prefillResponse?.drafts ?? {}
      // `{students: {id, job}}`, or the draft id alone.
      const draftOf = (value: any) =>
        typeof value === 'string' || typeof value === 'number'
          ? {id: value}
          : value?.id || value?.draftId
            ? {id: value.id ?? value.draftId, job: value.job}
            : null
      const drafts = {students: draftOf(raw.students), allocation: draftOf(raw.allocation)}
      if (!drafts.students && !drafts.allocation) return
      prefillAbortsRef.current.get(item.id)?.abort()
      const controller = new AbortController()
      prefillAbortsRef.current.set(item.id, controller)
      const cancelled = () => controller.signal.aborted
      patchFile(item.id, {carried: {checking: true, students: 0, allocation: 0}})

      // The finished job says how many rows it read (`counts.records`): no template call here,
      // which would also re-check allocations against teachers that aren't saved yet.
      const rowsIn = async (draft: any): Promise<number> => {
        if (!draft?.id) return 0
        const job = await waitForPrefillJob(dispatch, String(draft.id), cancelled)
        return prefillJobRowsFound(job)
      }
      Promise.all([rowsIn(drafts.students), rowsIn(drafts.allocation)]).then(
        ([students, allocation]) => {
          if (cancelled()) return
          prefillAbortsRef.current.delete(item.id)
          patchFile(item.id, {carried: {checking: false, students, allocation}})
        }
      )
    },
    [dispatch, patchFile]
  )

  /** Summary line under a file: translated from `counts`, else the server's own message. */
  const summarize = useCallback(
    (counts: DataUploadCounts | null, message: string | null) =>
      dataUploadCountsSummary(intl.formatMessage, counts, 'teachers') ?? message,
    [intl]
  )

  /**
   * Copy for a failed file. A known code (the file's `reason`, or `result.code` of a failed
   * call) gets its translated copy, with the count behind it for a row code
   * (`NAME_NOT_ENGLISH`: "4 names are not written in English letters…"). Otherwise the server's
   * `message`; then the call's HTTP status (413, 504, connection lost); then the generic
   * "could not extract".
   */
  const describeFailure = useCallback(
    (item: UploadItem, error: unknown, extraction?: TeacherExtraction): Partial<UploadItem> => {
      const code = extraction?.error ?? dataUploadErrorCode(error)
      const counts = extraction?.counts
      const failed = {
        status: 'error' as const,
        errorKey: ERROR_KEYS.extract,
        skipped: extraction?.skipped ?? [],
        summary: null,
      }
      if (code && code in DATA_UPLOAD_ERROR_KEYS) {
        return {
          ...failed,
          errorText: dataUploadErrorMessage(intl.formatMessage, code, {
            config: uploadConfig,
            useCaseKey: 'teachers',
            fileName: item.file.name,
            count: (code && counts?.leftOut[code]) || counts?.read || undefined,
          }),
        }
      }
      if (extraction?.message) return {...failed, errorText: extraction.message}
      if (!extraction && error !== undefined) {
        return {
          ...failed,
          errorText: dataUploadErrorMessage(intl.formatMessage, error, {
            config: uploadConfig,
            useCaseKey: 'teachers',
            fileName: item.file.name,
          }),
        }
      }
      return {...failed, errorText: undefined}
    },
    [intl, uploadConfig]
  )

  const applyExtraction = useCallback(
    (item: UploadItem, extraction: TeacherExtraction) => {
      if (extraction.error) {
        patchFile(item.id, describeFailure(item, undefined, extraction))
        return
      }
      if (extraction.rows.length === 0) {
        patchFile(item.id, {
          status: 'error',
          errorKey: ERROR_KEYS.noNames,
          errorText: extraction.message ?? undefined,
          skipped: extraction.skipped,
        })
        return
      }
      patchFile(item.id, {
        status: 'ready',
        rows: extraction.rows,
        summary: summarize(extraction.counts, extraction.message),
        skipped: extraction.skipped,
      })
    },
    [describeFailure, patchFile, summarize]
  )

  /**
   * Upload the file in pieces, then `extract-teachers` with its `uploadId`. The same upload
   * goes to `onboarding/prefill` (fire and forget): the Students and Allocation steps pick up
   * whatever else the file holds when they open.
   */
  const extractOnServer = useCallback(
    async (item: UploadItem) => {
      const controller = new AbortController()
      uploadAbortsRef.current.set(item.id, controller)
      patchFile(item.id, {status: 'uploading', progress: 0})
      let prefillSent = false
      try {
        const response = await withUploadedFile(
          dispatch,
          item.file,
          {
            useCaseKey: 'teachers',
            signal: controller.signal,
            onProgress: (progress) => patchFile(item.id, {progress}),
          },
          (uploadId) => {
            if (controller.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError')
            if (!prefillSent) {
              prefillSent = true
              dispatch(
                prefillOnboardingFromFile(
                  {uploadId},
                  (res: any) => watchCarried(item, res),
                  // A 202 the saga does not count as success still carries the drafts.
                  (res: any) => watchCarried(item, res)
                ) as never
              )
            }
            patchFile(item.id, {status: 'extracting'})
            const form = new FormData()
            form.append('uploadId', uploadId)
            return dispatchApi(dispatch, (ok, fail) => extractOnboardingTeachers(form, ok, fail))
          }
        )
        if (controller.signal.aborted) return
        applyExtraction(item, normalizeExtractionResponse(response, item.file.name))
      } catch (error) {
        if (isAbortError(error) || controller.signal.aborted) return
        // A failed call can still carry the file's result (reason, message, counts).
        const extraction = normalizeExtractionResponse(error, item.file.name)
        patchFile(item.id, describeFailure(item, error, extraction.error ? extraction : undefined))
      } finally {
        uploadAbortsRef.current.delete(item.id)
      }
    },
    [applyExtraction, describeFailure, dispatch, patchFile, watchCarried]
  )

  /** Local UI work only (`VITE_MOCK_ONBOARDING_TEACHER_EXTRACTION`): a fake answer, no upload. */
  const extractWithMock = useCallback(
    (item: UploadItem) => {
      patchFile(item.id, {status: 'extracting'})
      window.setTimeout(
        () =>
          applyExtraction(
            item,
            normalizeExtractionResponse(buildMockExtraction(item.file.name), item.file.name)
          ),
        900
      )
    },
    [applyExtraction, patchFile]
  )

  /** Excel-only servers: the sheet is parsed here, with the server's row rules. */
  const parseInBrowser = useCallback(
    async (item: UploadItem) => {
      const ext = fileExtension(item.file.name)
      let sheetRows: string[][]
      try {
        sheetRows =
          ext === 'csv' ? parseCsvRows(await item.file.text()) : await excelFileToRows(item.file)
      } catch {
        patchFile(item.id, {status: 'error', errorKey: ERROR_KEYS.read})
        return false
      }
      const parsed = parseTeachersRows(sheetRows, item.file.name)
      if (parsed.error) return true
      if (parsed.rows.length === 0) {
        // Every row broke a rule: say which one hit most, as the server does in `reason`.
        const {leftOut} = browserCounts(0, parsed.skipped)
        const [topCode, topCount] = Object.entries(leftOut).sort((a, b) => b[1] - a[1])[0] ?? []
        patchFile(item.id, {
          status: 'error',
          errorKey: ERROR_KEYS.noNames,
          skipped: parsed.skipped,
          errorText: topCode
            ? dataUploadErrorMessage(intl.formatMessage, topCode, {count: topCount})
            : undefined,
        })
        return false
      }
      patchFile(item.id, {
        status: 'ready',
        rows: parsed.rows,
        summary:
          parsed.skipped.length > 0
            ? summarize(browserCounts(parsed.rows.length, parsed.skipped), null)
            : null,
        skipped: parsed.skipped,
      })
      return false
    },
    [intl, patchFile, summarize]
  )

  const processFile = useCallback(
    async (item: UploadItem) => {
      if (extractionEnabled && !useMockExtraction && isChunkedUploadAvailable()) {
        await extractOnServer(item)
        return
      }
      if (item.kind === 'spreadsheet') {
        // `true`: no recognisable header, so the sheet can't be trusted as parsed.
        const unstructured = await parseInBrowser(item)
        if (!unstructured) return
        if (!extractionEnabled) {
          patchFile(item.id, {status: 'error', errorKey: ERROR_KEYS.noHeader})
          return
        }
      }
      extractWithMock(item)
    },
    [extractOnServer, extractWithMock, extractionEnabled, parseInBrowser, patchFile]
  )

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return
      // Single-file mode: a new file replaces whatever is queued instead of being refused
      // as "too many", so the user can just drop the corrected sheet again.
      const {accepted, rejected} = validateTeacherFiles(
        singleFile ? [] : files.map((f) => f.file),
        incoming,
        {extractionEnabled, maxFileBytes}
      )

      // One toast per reason, not one per file — a 30-screenshot drop must not spam.
      const reasons = new Set(rejected.map((r) => r.reason))
      reasons.forEach((reason) => {
        if (reason === 'unsupported' && extractionEnabled) {
          // Says which formats work, and asks for a re-save when it is a legacy .xls / .doc.
          const refused = rejected.find((r) => r.reason === 'unsupported')
          toast.error(
            dataUploadErrorMessage(intl.formatMessage, 'FILE_TYPE_NOT_ALLOWED', {
              fileName: refused?.file.name,
            })
          )
          return
        }
        toast.error(
          intl.formatMessage(
            {id: REJECTION_KEYS[reason]},
            {
              limit:
                reason === 'too-many' ? TEACHER_UPLOAD_LIMITS.maxFiles : formatBytes(maxFileBytes),
            }
          )
        )
      })

      const items: UploadItem[] = accepted.map((file) => ({
        id: nextUploadId(),
        file,
        kind: classifyTeacherFile(file, extractionEnabled) ?? 'document',
        status: 'parsing',
        rows: [],
      }))
      if (items.length === 0) return
      if (singleFile) {
        uploadAbortsRef.current.forEach((controller) => controller.abort())
        uploadAbortsRef.current.clear()
        prefillAbortsRef.current.forEach((controller) => controller.abort())
        prefillAbortsRef.current.clear()
      }
      setFiles((prev) => (singleFile ? items : [...prev, ...items]))
      items.forEach((item) => {
        void processFile(item)
      })
    },
    [extractionEnabled, files, intl, maxFileBytes, processFile]
  )

  const removeFile = (id: string) => {
    uploadAbortsRef.current.get(id)?.abort()
    uploadAbortsRef.current.delete(id)
    prefillAbortsRef.current.get(id)?.abort()
    prefillAbortsRef.current.delete(id)
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const openFilePicker = () => fileInputRef.current?.click()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? [])
    e.target.value = ''
    addFiles(list)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    addFiles(Array.from(e.dataTransfer.files ?? []))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragging) setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  // --- Review ---------------------------------------------------------------------

  const handleReview = () => {
    if (!canReview) return
    const fromFiles = files.flatMap((f) => (f.status === 'ready' ? f.rows : []))
    onReview([...namesToTeacherRows(names), ...fromFiles])
  }

  const reviewHintKey = isBusy
    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_BUSY'
    : canReview
      ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_ENABLED'
      : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_DISABLED'

  const renderFileStatus = (item: UploadItem) => {
    if (item.status === 'uploading') {
      const percent = Math.round((item.progress ?? 0) * 100)
      return (
        <span className='step4-te-file-status step4-te-file-status--busy'>
          <Loader2 size={14} className='step4-te-spin' aria-hidden />
          {intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_UPLOADING'},
            {percent}
          )}
        </span>
      )
    }
    if (item.status === 'parsing' || item.status === 'extracting') {
      return (
        <span className='step4-te-file-status step4-te-file-status--busy'>
          <Loader2 size={14} className='step4-te-spin' aria-hidden />
          {intl.formatMessage({
            id:
              item.status === 'parsing'
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_PARSING'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING',
          })}
        </span>
      )
    }
    if (item.status === 'error') {
      return (
        <span className='step4-te-file-status step4-te-file-status--error'>
          <AlertTriangle size={14} aria-hidden />
          {item.errorText ?? intl.formatMessage({id: item.errorKey ?? ERROR_KEYS.extract})}
        </span>
      )
    }
    const needsCheck = item.rows.filter((r) => r._needsReview).length
    return (
      <span className='step4-te-file-status step4-te-file-status--ready'>
        <CheckCircle2 size={14} aria-hidden />
        {intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_FOUND'},
          {count: item.rows.length}
        )}
        {needsCheck > 0 && (
          <span className='step4-te-file-status-check'>
            {' · '}
            {intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_NEEDS_CHECK'},
              {count: needsCheck}
            )}
          </span>
        )}
      </span>
    )
  }

  /** "This file also has 24 students...": what the later steps will be filled with. */
  const renderCarried = (item: UploadItem) => {
    const carried = item.carried
    if (!carried) return null
    if (carried.checking) {
      return (
        <span className='step4-te-file-carried step4-te-file-carried--checking'>
          <Loader2 size={12} className='step4-te-spin' aria-hidden />
          {intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.CARRIED_CHECKING',
          })}
        </span>
      )
    }
    return (
      <>
        {carried.students > 0 && (
          <span className='step4-te-file-carried'>
            <Info size={12} aria-hidden />
            {intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.CARRIED_STUDENTS'},
              {count: carried.students}
            )}
          </span>
        )}
        {carried.allocation > 0 && (
          <span className='step4-te-file-carried'>
            <Info size={12} aria-hidden />
            {intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.CARRIED_ALLOCATION'},
              {count: carried.allocation}
            )}
          </span>
        )}
      </>
    )
  }

  /** The rows a file left out, and why: collapsed under the file like the Students report. */
  const renderSkipped = (item: UploadItem) => {
    const skipped = item.skipped ?? []
    if (skipped.length === 0 || (item.status !== 'ready' && item.status !== 'error')) return null
    const unnamed = intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.UNNAMED_ROW'})
    return (
      <details className='step4-te-file-skipped'>
        <summary>
          {intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.LEFT_OUT'},
            {count: skipped.length}
          )}
        </summary>
        <ul>
          {skipped.slice(0, MAX_LISTED_SKIPPED).map((row, index) => {
            const key = dataUploadSkippedRowKey(row)
            const detail = key ? intl.formatMessage({id: key}) : row.message || row.reason
            return (
              <li key={row.recordId ?? `${row.label}-${index}`}>
                <strong>{row.label || unnamed}</strong>
                {detail ? <span> · {detail}</span> : null}
              </li>
            )
          })}
          {skipped.length > MAX_LISTED_SKIPPED && (
            <li className='step4-te-file-skipped-more'>
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.CONVERT.MORE_ROWS'},
                {count: skipped.length - MAX_LISTED_SKIPPED}
              )}
            </li>
          )}
        </ul>
      </details>
    )
  }

  return (
    <div className={`step4-teacher-entry${isDark ? ' step4-teacher-entry--dark' : ''}`}>
      <div className='step4-te-header'>
        <h2 className='step4-te-title'>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.HEADING'})}
        </h2>
        <p className='step4-te-subtitle'>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUBHEADING'})}
        </p>
        {existingCount > 0 && (
          <p className='step4-te-existing'>
            <Info size={14} aria-hidden />
            {intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.EXISTING_COUNT'},
              {count: existingCount}
            )}
          </p>
        )}
      </div>

      <div className='step4-te-grid'>
        {/* --- Add / paste names ------------------------------------------------ */}
        <section className='step4-te-card' aria-labelledby={`${textareaId}-title`}>
          <div className='step4-te-card-head'>
            <span className='step4-te-card-icon step4-te-card-icon--paste'>
              <ClipboardList size={22} aria-hidden />
            </span>
            <div className='step4-te-card-head-text'>
              <div className='step4-te-card-title-row'>
                <h3 id={`${textareaId}-title`} className='step4-te-card-title'>
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_TITLE',
                  })}
                </h3>
                <span className='step4-te-badge'>
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.RECOMMENDED',
                  })}
                </span>
              </div>
              <p className='step4-te-card-desc'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_DESCRIPTION',
                })}
              </p>
            </div>
          </div>

          <div className={`step4-te-lines${lineCount === 0 ? ' step4-te-lines--empty' : ''}`}>
            {lineCount > 0 && (
              <div className='step4-te-gutter' ref={gutterRef} aria-hidden>
                {Array.from({length: lineCount}, (_, i) => (
                  <div key={i} className='step4-te-gutter-line'>
                    {i + 1}
                  </div>
                ))}
              </div>
            )}
            <textarea
              id={textareaId}
              ref={textareaRef}
              className='step4-te-textarea'
              wrap='off'
              spellCheck={false}
              autoComplete='off'
              value={namesText}
              placeholder={intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_PLACEHOLDER',
              })}
              onChange={(e) => setNamesText(e.target.value)}
              onPaste={handlePaste}
              onScroll={syncGutterScroll}
            />
          </div>

          <div className='step4-te-help'>
            {names.length > 0 && (
              <p className='step4-te-help-count'>
                {intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_COUNT'},
                  {count: names.length}
                )}
              </p>
            )}
            <p>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_HELP_SEPARATORS',
              })}
            </p>
            <p>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_HELP_DUPLICATES',
              })}
            </p>
          </div>
        </section>

        {/* --- Upload ------------------------------------------------------------ */}
        <section className='step4-te-card' aria-labelledby={`${textareaId}-upload-title`}>
          <div className='step4-te-card-head'>
            <span className='step4-te-card-icon step4-te-card-icon--upload'>
              <FileSpreadsheet size={22} aria-hidden />
            </span>
            <div className='step4-te-card-head-text'>
              <h3 id={`${textareaId}-upload-title`} className='step4-te-card-title'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_TITLE',
                })}
              </h3>
              <p className='step4-te-card-desc'>
                {intl.formatMessage({
                  id: extractionEnabled
                    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_DESCRIPTION_ANY'
                    : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_DESCRIPTION',
                })}
              </p>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type='file'
            multiple={!singleFile}
            accept={teacherUploadAccept(extractionEnabled)}
            className='d-none'
            onChange={handleInputChange}
          />

          <div
            role='button'
            tabIndex={0}
            className={`step4-te-dropzone${isDragging ? ' step4-te-dropzone--active' : ''}`}
            onClick={openFilePicker}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openFilePicker()
              }
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <span className='step4-te-dropzone-icon'>
              <CloudUpload size={26} aria-hidden />
            </span>
            <p className='step4-te-dropzone-title'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROPZONE_TITLE',
              })}
            </p>
            <p className='step4-te-dropzone-formats'>
              {intl.formatMessage({
                id: extractionEnabled
                  ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROPZONE_FORMATS_ANY'
                  : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROPZONE_FORMATS',
              })}
            </p>
            <p className='step4-te-dropzone-limits'>
              {intl.formatMessage(
                {
                  id: extractionEnabled
                    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_LIMITS_ANY'
                    : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_LIMITS',
                },
                {
                  files: TEACHER_UPLOAD_LIMITS.maxFiles,
                  perFile: formatBytes(maxFileBytes),
                  total: formatBytes(maxFileBytes * TEACHER_UPLOAD_LIMITS.maxFiles),
                }
              )}
            </p>
            <button
              type='button'
              className='step4-te-choose-btn'
              onClick={(e) => {
                e.stopPropagation()
                openFilePicker()
              }}
            >
              <CloudUpload size={16} aria-hidden />
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.CHOOSE_FILES',
              })}
            </button>
            <button
              type='button'
              className='step4-te-template-link'
              onClick={(e) => {
                e.stopPropagation()
                onDownloadTemplate()
              }}
            >
              <Download size={14} aria-hidden />
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DOWNLOAD_TEMPLATE',
              })}
            </button>
          </div>

          {files.length > 0 && (
            <ul className='step4-te-files' aria-live='polite'>
              {files.map((item) => (
                <li key={item.id} className={`step4-te-file step4-te-file--${item.status}`}>
                  <span className='step4-te-file-icon'>
                    <FileKindIcon kind={item.kind} />
                  </span>
                  <span className='step4-te-file-body'>
                    <span className='step4-te-file-name' title={item.file.name}>
                      {item.file.name}
                    </span>
                    <span className='step4-te-file-meta'>
                      <span className='step4-te-file-size'>{formatBytes(item.file.size)}</span>
                      {' · '}
                      {renderFileStatus(item)}
                    </span>
                    {item.status === 'uploading' && (
                      <span
                        className='step4-te-file-progress'
                        role='progressbar'
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round((item.progress ?? 0) * 100)}
                      >
                        <span style={{width: `${Math.round((item.progress ?? 0) * 100)}%`}} />
                      </span>
                    )}
                    {item.status === 'extracting' && (
                      <span className='step4-te-file-hint'>
                        {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})}
                      </span>
                    )}
                    {item.status === 'ready' && item.summary && (
                      <span className='step4-te-file-hint'>{item.summary}</span>
                    )}
                    {renderSkipped(item)}
                    {renderCarried(item)}
                  </span>
                  <button
                    type='button'
                    className='step4-te-file-remove'
                    onClick={() => removeFile(item.id)}
                    aria-label={intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REMOVE_FILE',
                    })}
                    title={intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REMOVE_FILE',
                    })}
                  >
                    <X size={16} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className='step4-te-note'>
            <span className='step4-te-note-icon'>
              <Info size={18} aria-hidden />
            </span>
            <ul className='step4-te-note-list'>
              <li>
                {intl.formatMessage({
                  id: extractionEnabled
                    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_NOTE_EXTRACT_ANY'
                    : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_NOTE_EXTRACT',
                })}
              </li>
              {extractionEnabled && (
                <>
                  <li>{intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})}</li>
                  <li>{intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.PHOTO_TIPS'})}</li>
                </>
              )}
              <li>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_NOTE_REVIEW',
                })}
              </li>
            </ul>
          </div>
        </section>
      </div>

      <div className='step4-te-footer'>
        {showBackButton && (
          <button type='button' className='onboarding-btn-back' onClick={onBack}>
            <ArrowLeft size={16} className='me-2' />
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
          </button>
        )}
        <div className='step4-te-footer-cta'>
          <button
            type='button'
            className='onboarding-btn-next'
            disabled={!canReview}
            onClick={handleReview}
          >
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_BTN'})}
            <ArrowRight size={16} className='ms-2' />
          </button>
          <span className='step4-te-footer-hint'>{intl.formatMessage({id: reviewHintKey})}</span>
        </div>
      </div>
    </div>
  )
}

export default Step4TeacherEntry
